import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { rewampPalette } from "../rewampTheme";

// CSS-variable bridge — publishes the centralised rewampTheme tokens that the
// global accessibility stylesheet (src/styles/accessibility.css) consumes, so
// the effect colours stay sourced from rewampTheme.js rather than duplicated as
// hex literals in CSS. Add a new var here when a CSS rule needs another token.
const CSS_VAR_TOKENS = {
  "--bk-link-highlight-bg": rewampPalette.accent.butterscotchBg, // #FAEBC7
  "--bk-link-highlight-fg": rewampPalette.neutral.headings, // #11151E
  "--bk-img-alt-bg": rewampPalette.neutral.chipBg, // #F0EFEB — alt-text placeholder fill
  "--bk-img-alt-border": rewampPalette.neutral.stroke, // #E0E0D8 — placeholder frame
  // Invert Colors paints this onto the filtered root so the page canvas (the
  // browser default behind transparent containers) inverts WITH the content,
  // instead of staying white in the gutters. Light surface base = paper.
  "--bk-invert-canvas": rewampPalette.background.paper, // #FFFFFF
};

// Below this rendered size (width OR height, px) an <img> is treated as a
// decorative UI icon (nav glyph, category pill, button icon) and kept visible
// when "Hide Images" is on — per GIGW 3.0 / WCAG 2.1 AA, decorative imagery
// shouldn't be stripped. Only larger content images are hidden/replaced.
const ICON_MAX_PX = 40;

// ── Hide Images (imperative) ────────────────────────────────────────────────
// Unlike the other prefs (attribute → CSS reacts), "Hide Images" works per-image
// because the decision depends on the image's RENDERED SIZE, which CSS can't
// query. We strip src/srcset IN PLACE and tag the node with a data-attr that
// accessibility.css reacts to — node identity is preserved, so React's
// reconciler isn't disturbed (avoids the removeChild crash that inserting or
// removing sibling nodes would cause). Per image:
//   • small (≤ICON_MAX_PX, decorative UI icon) → kept visible, marked a11yKept
//   • large + meaningful alt → src stripped, mode "chip" → CSS renders the alt
//     as a clean dashed placeholder (no broken-image glyph, alt clamped)
//   • large + no alt (decorative) → src stripped, mode "hide" → CSS display:none
// Scope: <img>/<picture> only — CSS background-images and inline SVG are out of
// scope. Pass-through for any element marked [data-a11y-skip].
const renderedSize = (img) => ({
  w: img.offsetWidth || img.naturalWidth || 0,
  h: img.offsetHeight || img.naturalHeight || 0,
});

// True once we can confidently classify it as a small decorative icon.
const isSmallIcon = (img) => {
  const { w, h } = renderedSize(img);
  if (!w && !h) return false; // size not yet known — don't decide yet
  return (w > 0 && w <= ICON_MAX_PX) || (h > 0 && h <= ICON_MAX_PX);
};

const stripImage = (img) => {
  if (img.dataset.a11yHidden || img.dataset.a11yKept) return;
  if (img.closest("[data-a11y-skip]")) return;

  // Author opt-in ([data-a11y-fit]): "hide this image as a box-fit chip whatever
  // its size". It bypasses the small-icon exemption (so e.g. the ~40px masthead
  // logos hide too) and the size-unknown defer (classification never depends on
  // measurement — a box-fit chip always tracks the element's own box).
  const forceFit = img.hasAttribute("data-a11y-fit");

  // Small UI icons stay visible — unless explicitly opted into hiding above.
  if (!forceFit && isSmallIcon(img)) {
    img.dataset.a11yKept = "1";
    return;
  }
  // Size unknown and not loaded → defer; the load listener re-runs this.
  const { w, h } = renderedSize(img);
  if (!forceFit && !w && !h && !img.complete) return;

  const alt = img.getAttribute("alt");
  img.dataset.a11yHidden = "1";
  img.dataset.a11yMode = alt ? "chip" : "hide";
  // Full label on hover — the chip text is ellipsis-clipped (heavily so in a
  // box-fit placeholder), so expose it as a native `title` tooltip. Skip when the
  // author already set a title: a component that keeps `title` in sync with the
  // language should own it (see RewampMasthead / RewampCarouselComponent).
  if (alt && !img.title) {
    img.title = alt;
    img.dataset.a11yTitleSet = "1";
  }
  if (img.hasAttribute("src")) {
    img.dataset.a11ySrc = img.getAttribute("src");
    img.removeAttribute("src");
  }
  if (img.hasAttribute("srcset")) {
    img.dataset.a11ySrcset = img.getAttribute("srcset");
    img.removeAttribute("srcset");
  }
  const picture = img.closest("picture");
  if (picture) {
    picture.querySelectorAll("source[srcset]").forEach((s) => {
      s.dataset.a11ySrcset = s.getAttribute("srcset");
      s.removeAttribute("srcset");
    });
  }
};

const restoreImage = (img) => {
  if (img.dataset.a11yHidden) {
    if (img.dataset.a11ySrc != null) {
      img.setAttribute("src", img.dataset.a11ySrc);
      delete img.dataset.a11ySrc;
    }
    if (img.dataset.a11ySrcset != null) {
      img.setAttribute("srcset", img.dataset.a11ySrcset);
      delete img.dataset.a11ySrcset;
    }
    const picture = img.closest("picture");
    if (picture) {
      picture.querySelectorAll("source").forEach((s) => {
        if (s.dataset.a11ySrcset != null) {
          s.setAttribute("srcset", s.dataset.a11ySrcset);
          delete s.dataset.a11ySrcset;
        }
      });
    }
    if (img.dataset.a11yTitleSet) {
      img.removeAttribute("title");
      delete img.dataset.a11yTitleSet;
    }
    delete img.dataset.a11yHidden;
    delete img.dataset.a11yMode;
  }
  delete img.dataset.a11yKept;
  delete img.dataset.a11yPending;
};

/**
 * AccessibilityProvider — single source of truth for the Ctrl+F2 accessibility
 * panel preferences (see .agent/accessibility/functional-effects.md).
 *
 * Design (per .agent/accessibility/README.md "Architectural plan"):
 *   • preference state lives here, persisted to localStorage
 *   • each preference is reflected as a `data-a11y-*` attribute on <html>
 *   • all visual behaviour is driven by CSS in src/styles/accessibility.css
 *     reacting to those attributes — this keeps the dialog purely declarative
 *
 * Font-size (Bigger/Smaller Text) and colour-mode (Light-Dark) are NOT owned
 * here — they already live in App.js (AppLayoutContext / ThemeContext) and the
 * dialog wires to those directly.
 *
 * Each preference maps to one DOM attribute. `null`/`false` removes it.
 */
const STORAGE_KEY = "bk-a11y-prefs";

// key → { attr, valueFor(prefValue) }. valueFor returns the attribute value, or
// null to remove the attribute entirely (the "off" state).
const ATTR_MAP = {
  textSpacing: { attr: "data-a11y-text-spacing", valueFor: (v) => (v ? "loose" : null) },
  lineHeight: { attr: "data-a11y-line-height", valueFor: (v) => (v ? "tall" : null) },
  // tri-state: "off" | "low" | "gray"
  saturation: { attr: "data-a11y-saturation", valueFor: (v) => (v && v !== "off" ? v : null) },
  invert: { attr: "data-a11y-invert", valueFor: (v) => (v ? "on" : null) },
  highlightLinks: { attr: "data-a11y-highlight-links", valueFor: (v) => (v ? "on" : null) },
  bigCursor: { attr: "data-a11y-cursor", valueFor: (v) => (v ? "large" : null) },
  pauseMotion: { attr: "data-a11y-pause-motion", valueFor: (v) => (v ? "on" : null) },
  hideImages: { attr: "data-a11y-hide-images", valueFor: (v) => (v ? "on" : null) },
};

const DEFAULT_PREFS = {
  textSpacing: false,
  lineHeight: false,
  saturation: "off",
  invert: false,
  highlightLinks: false,
  bigCursor: false,
  pauseMotion: false,
  hideImages: false,
};

const SATURATION_CYCLE = ["off", "low", "gray"];

const readStored = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    // Merge over defaults so a newly-added key is never `undefined`.
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_PREFS;
  }
};

const AccessibilityContext = createContext({
  prefs: DEFAULT_PREFS,
  toggle: () => {},
  cycleSaturation: () => {},
  reset: () => {},
  isActive: () => false,
});

export const AccessibilityProvider = ({ children }) => {
  const [prefs, setPrefs] = useState(readStored);

  // Publish rewampTheme tokens as CSS variables on <html> once, so the global
  // accessibility stylesheet can reference var(--bk-*) instead of hardcoding hex.
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(CSS_VAR_TOKENS).forEach(([name, value]) =>
      root.style.setProperty(name, value)
    );
  }, []);

  // Hide Images is imperative (see stripImage/restoreImage): strip src on every
  // content <img> so its alt text shows, and keep doing so for images React
  // mounts later (carousels, lazy lists) via a MutationObserver. On toggle-off
  // or unmount, restore every stripped image.
  useEffect(() => {
    if (!prefs.hideImages) return undefined;
    const root = document.body;
    if (!root) return undefined;
    let applying = false;
    let cancelled = false;

    // Images whose size isn't known yet (lazy/carousel, not laid out) can't be
    // classified at mutation time — naturalWidth is 0 until they load. We attach
    // a one-shot load listener so they get re-classified once their real size
    // is available; `cancelled` guards against stripping after toggle-off.
    const onImgLoad = (e) => {
      if (cancelled) return;
      applying = true;
      stripImage(e.currentTarget);
      applying = false;
    };

    const applyAll = () => {
      applying = true;
      root.querySelectorAll("img").forEach((img) => {
        stripImage(img);
        if (
          !cancelled &&
          !img.dataset.a11yHidden &&
          !img.dataset.a11yKept &&
          !img.dataset.a11yPending &&
          !img.complete
        ) {
          img.dataset.a11yPending = "1";
          img.addEventListener("load", onImgLoad, { once: true });
        }
      });
      applying = false;
    };
    applyAll();
    const observer = new MutationObserver((mutations) => {
      if (applying) return; // guard against reacting to our own writes
      if (mutations.some((m) => m.addedNodes.length)) applyAll();
    });
    observer.observe(root, { childList: true, subtree: true });
    return () => {
      cancelled = true;
      observer.disconnect();
      root.querySelectorAll("img").forEach((img) => {
        img.removeEventListener("load", onImgLoad);
        if (img.dataset.a11yHidden || img.dataset.a11yKept || img.dataset.a11yPending) {
          restoreImage(img);
        }
      });
    };
  }, [prefs.hideImages]);

  // Reflect every preference onto <html> as a data-attribute, and persist.
  // Runs on mount too, so stored prefs are re-applied on a fresh page load.
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(ATTR_MAP).forEach(([key, { attr, valueFor }]) => {
      const value = valueFor(prefs[key]);
      if (value == null) root.removeAttribute(attr);
      else root.setAttribute(attr, value);
    });
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      /* storage may be unavailable (private mode); the DOM state still applies */
    }
  }, [prefs]);

  const toggle = useCallback((key) => {
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const cycleSaturation = useCallback(() => {
    setPrefs((prev) => {
      const next = SATURATION_CYCLE[(SATURATION_CYCLE.indexOf(prev.saturation) + 1) % SATURATION_CYCLE.length];
      return { ...prev, saturation: next };
    });
  }, []);

  const reset = useCallback(() => {
    // Clear persisted state and let the effect strip every data-attribute.
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setPrefs(DEFAULT_PREFS);
  }, []);

  // A preference is "active" (drives aria-pressed) when it is not its off value.
  const isActive = useCallback(
    (key) => (key === "saturation" ? prefs.saturation !== "off" : Boolean(prefs[key])),
    [prefs]
  );

  const value = useMemo(
    () => ({ prefs, toggle, cycleSaturation, reset, isActive }),
    [prefs, toggle, cycleSaturation, reset, isActive]
  );

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => useContext(AccessibilityContext);

export default AccessibilityContext;
